import React, { useState, useMemo } from 'react';
import { Student, InstallmentItem } from '../../types/db';
import { Search, Wallet, CheckCircle, Loader2, Calendar, Banknote, History, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

interface BillingManagerProps {
  students: Student[];
  branchId?: string;
  onRefresh: () => void;
}

export function BillingManager({ students, onRefresh }: BillingManagerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [payForm, setPayForm] = useState({
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    installmentIndex: -1,
  });

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const matchSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
      const totalCost = s.financial_record?.total_cost || 0;
      const paid =
        s.financial_record?.payments?.reduce(
          (a: number, b: any) => a + (Number(b.amount) || 0),
          0
        ) || 0;
      const debt = totalCost - paid;
      return matchSearch && debt > 0;
    });
  }, [students, searchTerm]);

  const handleOpenPay = (student: Student, instIndex: number, amount: number, label: string) => {
    setSelectedStudent(student);
    setPayForm({
      amount: amount,
      date: new Date().toISOString().split('T')[0],
      notes: `Pelunasan ${label}`,
      installmentIndex: instIndex,
    });
    setIsPayModalOpen(true);
  };

  const processPayment = async () => {
    if (!selectedStudent || payForm.amount <= 0) return;

    setLoading(true);
    try {
      // 🚀 PANGGIL EDGE FUNCTION BARU (INTEGRASI PENUH)
      const { error } = await supabase.functions.invoke('record-cash-payment', {
        body: {
          student_id: selectedStudent.id,
          branch_id: selectedStudent.branch_id,
          amount: payForm.amount,
          date: payForm.date,
          notes: payForm.notes,
        },
      });

      if (error) throw error;

      window.alert('Pembayaran tunai berhasil dicatat!');
      setIsPayModalOpen(false);
      setSelectedStudent(null);
      onRefresh();
    } catch (err: any) {
      window.alert('Gagal mencatat pembayaran: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
            <Wallet className="text-indigo-600 w-5 h-5" /> Kontrol Piutang Siswa
          </h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            Daftar siswa dengan tunggakan angsuran aktif
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 w-4 h-4" />
          <input
            placeholder="Cari nama siswa..."
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-50 rounded-2xl text-[11px] font-bold outline-none focus:border-indigo-100 transition-all bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStudents.map((s) => {
          const totalCost = s.financial_record?.total_cost || 0;
          const paid =
            s.financial_record?.payments?.reduce(
              (a: number, b: any) => a + (Number(b.amount) || 0),
              0
            ) || 0;
          const debt = totalCost - paid;
          const progress = totalCost > 0 ? Math.round((paid / totalCost) * 100) : 0;
          const nextInst = s.financial_record?.installment_plan?.find(
            (p: any) => p.status !== 'PAID'
          );

          return (
            <div
              key={s.id}
              className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden flex flex-col justify-between"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-rose-50 rounded-bl-full opacity-50 -mr-10 -mt-10 group-hover:scale-110 transition-transform"></div>

              <div className="relative z-10">
                <div className="flex justify-between items-start mb-6">
                  <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center font-black text-lg uppercase shadow-inner group-hover:bg-indigo-600 group-hover:text-white transition-all">
                    {s.name.charAt(0)}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest">
                      Sisa Tagihan
                    </p>
                    <p className="text-xl font-black text-slate-900 tracking-tighter">
                      Rp {debt.toLocaleString()}
                    </p>
                  </div>
                </div>

                <h4 className="font-black text-base text-slate-900 uppercase truncate mb-1">
                  {s.name}
                </h4>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-6">
                  {s.grade} • Cabang {s.branch_id}
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex justify-between text-[10px] font-bold">
                    <span className="text-slate-400">Progress Pembayaran</span>
                    <span className="text-indigo-600">{progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                    <div
                      className="h-full bg-emerald-500 transition-all duration-1000"
                      style={{ width: `${Math.min(100, progress)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 relative z-10">
                {nextInst ? (
                  <button
                    onClick={() => {
                      const idx = s.financial_record?.installment_plan?.indexOf(nextInst) ?? -1;
                      handleOpenPay(s, idx, nextInst.amount, nextInst.label);
                    }}
                    className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Banknote className="w-4 h-4" /> BAYAR {nextInst.label.toUpperCase()}
                  </button>
                ) : (
                  <div className="w-full bg-emerald-50 text-emerald-600 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest text-center border border-emerald-100">
                    Selesai / Lunas ✅
                  </div>
                )}

                <button
                  onClick={() => setSelectedStudent(s)}
                  className="w-full bg-slate-50 text-slate-400 py-3 rounded-2xl font-black uppercase text-[9px] tracking-widest hover:bg-slate-100 hover:text-slate-600 transition-all"
                >
                  Rincian Angsuran
                </button>
              </div>
            </div>
          );
        })}

        {filteredStudents.length === 0 && (
          <div className="col-span-full py-40 text-center border-4 border-dashed rounded-[4rem] border-slate-50 flex flex-col items-center justify-center bg-white/50">
            <CheckCircle className="w-16 h-16 text-emerald-200 mb-4" />
            <p className="font-black uppercase tracking-[0.3em] text-xs text-slate-300">
              Semua Tagihan Telah Lunas
            </p>
          </div>
        )}
      </div>

      {isPayModalOpen && selectedStudent && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white p-10 rounded-[3rem] w-full max-w-md space-y-8 shadow-2xl border border-white animate-fade-in-up">
            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Banknote className="w-10 h-10" />
              </div>
              <h3 className="font-black text-2xl uppercase tracking-tighter text-slate-900">
                Pembayaran Tunai
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                Siswa: {selectedStudent.name}
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                  Tanggal Pembayaran
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none" />
                  <input
                    type="date"
                    className="w-full border-2 border-slate-50 pl-12 pr-4 py-4 rounded-2xl font-bold bg-slate-50/50 outline-none focus:border-indigo-100 transition-all text-sm"
                    value={payForm.date}
                    onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                  Nominal Bayar (Rp)
                </label>
                <input
                  type="number"
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black text-2xl text-indigo-900 bg-slate-50/50 outline-none"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: Number(e.target.value) })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                  Catatan
                </label>
                <input
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-bold bg-slate-50/50 outline-none text-sm"
                  placeholder="Misal: Pelunasan Angsuran 2"
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                />
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  onClick={() => setIsPayModalOpen(false)}
                  className="flex-1 bg-slate-100 text-slate-400 py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest"
                >
                  Batal
                </button>
                <button
                  onClick={processPayment}
                  disabled={loading}
                  className="flex-[2] bg-indigo-600 text-white py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest shadow-xl flex justify-center items-center gap-3"
                >
                  {loading ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}{' '}
                  SIMPAN
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedStudent && !isPayModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white p-10 rounded-[3rem] w-full max-w-4xl shadow-2xl border border-white animate-fade-in-up relative max-h-[90vh] overflow-y-auto no-scrollbar">
            <button
              onClick={() => setSelectedStudent(null)}
              className="absolute top-8 right-8 text-slate-300 hover:text-slate-900 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-5 mb-10">
              <div className="w-16 h-16 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl">
                <History className="w-8 h-8" />
              </div>
              <div>
                <h3 className="font-black text-2xl uppercase tracking-tighter text-slate-900">
                  Rencana Angsuran
                </h3>
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                  {selectedStudent.name} • {selectedStudent.grade}
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-[2.5rem] border border-slate-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100/50 text-slate-400 font-black uppercase tracking-widest text-[9px] border-b">
                  <tr>
                    <th className="p-6 text-left">Deskripsi</th>
                    <th className="p-6 text-right">Nominal</th>
                    <th className="p-6 text-center">Status</th>
                    <th className="p-6 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(selectedStudent.financial_record?.installment_plan || []).map(
                    (item: InstallmentItem, idx: number) => (
                      <tr key={idx} className="font-bold text-slate-700">
                        <td className="p-6 uppercase">{item.label}</td>
                        <td className="p-6 text-right font-black">
                          Rp {item.amount.toLocaleString()}
                        </td>
                        <td className="p-6 text-center">
                          <span
                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                              item.status === 'PAID'
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                : 'bg-rose-50 text-rose-500 border-rose-100'
                            }`}
                          >
                            {item.status === 'PAID' ? 'LUNAS' : 'BELUM BAYAR'}
                          </span>
                        </td>
                        <td className="p-6 text-center">
                          {item.status !== 'PAID' && (
                            <button
                              onClick={() =>
                                handleOpenPay(selectedStudent, idx, item.amount, item.label)
                              }
                              className="bg-indigo-600 text-white px-5 py-2 rounded-xl font-black text-[9px] uppercase hover:bg-indigo-700"
                            >
                              BAYAR
                            </button>
                          )}
                          {item.status === 'PAID' && (
                            <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
